import { Controller } from "react-hook-form";
import PhoneInputWithCountry from "react-phone-number-input";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import clsx from "clsx";
import dropdownArrow from "@assets/svgs/uncollapse.svg";

// Custom validation that allows 555 area codes for US numbers
const customIsValidPhoneNumber = (value?: string): boolean => {
  if (!value) return false;

  // Check if it's a US +1 number with 555 area code
  const us555Pattern = /^\+1\s*\(?\s*555\s*\)?\s*\d{3}\s*[-\s]?\d{4}$/;
  if (us555Pattern.test(value)) {
    return true;
  }

  // For all other numbers, check if it's at least a possible phone number
  try {
    return isPossiblePhoneNumber(value);
  } catch {
    return false;
  }
};

// Custom arrow component
const CustomArrow = () => (
  <img
    src={dropdownArrow}
    alt="dropdown arrow"
    className="phone-input-arrow"
  />
);

interface IPhoneInputProps {
  name: string;
  control: any;
  errors: any;
  label?: string | React.ReactNode;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  mainContainerClassName?: string;
  defaultCountry?: string;
}

const PhoneInput = ({
  name,
  control,
  errors,
  label,
  placeholder = "Phone number...",
  className,
  labelClassName,
  disabled,
  mainContainerClassName,
  defaultCountry = "US",
}: IPhoneInputProps) => {
  return (
    <div className={`${mainContainerClassName}`}>
      {label && (
        <label
          htmlFor={name}
          className={`block ${
            labelClassName
              ? labelClassName
              : "text-sm font-medium text-gray3 text-start pb-[11px]"
          }`}
        >
          {label}
        </label>
      )}
      <Controller
        name={name ?? ""}
        control={control}
        render={({ field }) => (
          <PhoneInputWithCountry
            {...field}
            defaultCountry={defaultCountry as any}
            disabled={disabled}
            placeholder={placeholder}
            countrySelectArrowComponent={CustomArrow}
            isValidPhoneNumber={customIsValidPhoneNumber}
            className={clsx(
              "phone-input-custom mt-1 block w-full [&_input]:caret-green-600 [&_input]:focus:caret-green-600",
              {
                "border-red-500 border-[2px]": errors && errors[name],
                "border-gray5": !errors || !errors[name],
                "hover:border-gray4 hover:border-[1.5px]":
                  !errors || !errors[name],
                "border-[#BCC9DE] bg-gray-100 cursor-not-allowed": disabled,
              },
              className
            )}
          />
        )}
      />
      {errors && errors[name] && (
        <p className="text-white ml-2 mr-1 text-sm text-start mt-1 px-[14px] py-[4px] bg-red2 rounded-tr-[20px] rounded-br-[20px] rounded-bl-[20px]">
          {errors[name]?.message?.toString()}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
